import { useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useExperimentExposedEvent } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import Banner from '@zenbusiness/zui/Banner';
import DismissBanner from '@zenbusiness/zui/DismissBanner';
import SharedAsset from '@zenbusiness/zui/SharedAsset';

import { ErrorBoundary } from '#client/components/ErrorBoundary';
import { MfaNudgeBanner } from '#client/components/MfaNudgeBanner';
import { RELEASE_ACCOUNT_SETTINGS_MFA } from '#client/constants/experimentsFlags';
import { useBanners } from '#client/hooks/useBanners';

import * as Styled from './HomeBanners.style';

const APP_SECTION = 'home';
const APP_PAGE_NAME = 'dashboard_home_page';

/**
 * Primary CTA link for banners that carry their own clickstream element name. zui's built-in
 * banner action always reports elementName `banner_cta`, so it can't send the server's name.
 *
 * @param {object} props - Banner slot props; the rest go to Button.
 * @param {string} [props.bannerColor] - Dropped; Button picks its own color.
 * @param {import('react').ReactNode} [props.bannerTitle] - Dropped; the title is already in clickstreamProps.
 * @param {import('react').ReactNode} props.label - Button text.
 * @returns {import('react').ReactElement}
 */
function ClickstreamCta({ bannerColor, bannerTitle, label, ...delegated }) {
  return (
    <Styled.ClickstreamCta variant="contained" {...delegated}>
      {label}
    </Styled.ClickstreamCta>
  );
}

ClickstreamCta.propTypes = {
  bannerColor: PropTypes.string,
  bannerTitle: PropTypes.node,
  label: PropTypes.node,
};

function HomeBanners({ onOpenModal }) {
  const { data, error, inWfcPastDueBannerCtaExperiment, loading } = useBanners();
  const isMfaReleased = useExperimentFlag(RELEASE_ACCOUNT_SETTINGS_MFA, false);
  const banner = data?.[0];
  const featureFlagKey = banner?.experiment?.featureFlagKey;
  const featureFlagVariation = banner?.experiment?.featureFlagVariation;
  const isExperimentBanner = Boolean(featureFlagKey && featureFlagVariation);
  const isDismissed = banner?.dismissible && banner?.storageKey && localStorage.getItem(banner.storageKey) === 'true';
  const trackExperimentExposed = useExperimentExposedEvent({
    featureFlagKey,
    featureFlagVariation,
    inExperiment: inWfcPastDueBannerCtaExperiment,
  });

  const handleCtaClick = useCallback(
    (cta = {}) => {
      if (cta.modalName) {
        onOpenModal(cta.modalName);
      }
    },
    [onOpenModal],
  );

  useEffect(() => {
    if (!loading && !error && isExperimentBanner && !isDismissed) {
      trackExperimentExposed();
    }
  }, [error, isDismissed, isExperimentBanner, loading, trackExperimentExposed]);

  if (loading || error) {
    return null;
  }

  if (!banner || isDismissed) {
    return isMfaReleased ? (
      <Styled.HomeBanners>
        <ErrorBoundary id="src/Home#MfaNudgeBanner">
          <MfaNudgeBanner />
        </ErrorBoundary>
      </Styled.HomeBanners>
    ) : null;
  }

  const experimentDynamicProps = isExperimentBanner ? { featureFlagKey, featureFlagVariation } : undefined;
  // Server-provided analytics for this banner (ECOMM-2768); absent for most banners.
  const bannerClickstream = banner.clickstream;
  const dynamicProperties =
    bannerClickstream?.dynamicProperties || experimentDynamicProps
      ? { ...bannerClickstream?.dynamicProperties, ...experimentDynamicProps }
      : undefined;

  const createAction = (cta) => ({
    label: cta.label,
    ...(dynamicProperties && {
      clickstreamProps: {
        dynamicProperties,
      },
    }),
    ...(cta.modalName && { onClick: () => handleCtaClick(cta) }),
    ...(cta.url && { href: cta.url }),
  });

  const primaryAction = banner.primaryCta && createAction(banner.primaryCta);
  const hasClickstreamCta = Boolean(bannerClickstream?.ctaElementName && primaryAction);

  const slotProps = {
    ...(primaryAction && {
      primaryAction: hasClickstreamCta
        ? {
            ...primaryAction,
            clickstreamProps: {
              elementName: bannerClickstream.ctaElementName,
              elementValue: banner.title,
              appSection: APP_SECTION,
              appPageName: APP_PAGE_NAME,
              ...(dynamicProperties && { dynamicProperties }),
            },
          }
        : primaryAction,
    }),
    ...(banner.secondaryCta && { secondaryAction: createAction(banner.secondaryCta) }),
  };

  const slots = {
    ...(hasClickstreamCta && { primaryAction: ClickstreamCta }),
    ...(banner.imageUrl && {
      startIcon: <SharedAsset alt="start-icon-banner" src={banner.imageUrl} />,
    }),
  };

  const BannerComponent = banner.dismissible ? DismissBanner : Banner;

  return (
    <Styled.HomeBanners>
      <ErrorBoundary id="src/Home#Banners">
        <BannerComponent
          clickstreamProps={{
            appSection: APP_SECTION,
            ...(bannerClickstream?.elementName && {
              elementName: bannerClickstream.elementName,
              appPageName: APP_PAGE_NAME,
            }),
            ...(dynamicProperties && { dynamicProperties }),
          }}
          title={banner.title}
          subtitle={banner.description}
          variant={banner.type}
          slotProps={slotProps}
          slots={slots}
          {...(banner.dismissible && banner.storageKey && { storageKey: banner.storageKey })}
        />
      </ErrorBoundary>
    </Styled.HomeBanners>
  );
}

HomeBanners.propTypes = {
  onOpenModal: PropTypes.func,
};

export default HomeBanners;
